import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { getScanHistory, recordScan } from '@/mocks/scanHistory';
import { createStudySession } from '@/mocks/studySessions';
import { useTheme } from '@/contexts/ThemeContext';
import { useAIPreferences, getNarrationSpeedValue } from '@/contexts/AIPreferencesContext';
import { useUsage } from '@/contexts/UsageContext';
import Pressable from '@/components/base/Pressable';
import ScanVideoPlayer, { type ScanVideoPlayerHandle } from './components/ScanVideoPlayer';
import SaveSessionModal from '../components/SaveSessionModal';
import { useSavedAIResult, SavedLessonUnavailable } from '@/lib/useSavedAIResult';
import { useAuth } from '@/contexts/AuthContext';
import { createChatConversation } from '@/lib/chatHistory';
import { getStudyScope } from '@/lib/studyStorage';

function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function ScanResultVideo() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { preferences } = useAIPreferences();
  const { tier } = useUsage();
  const { user } = useAuth();
  const narrationSpeed = getNarrationSpeedValue(preferences.narrationSpeed);

  const [currentTime, setCurrentTime] = useState(0);
  const [isGenerating] = useState(false);
  const [progress] = useState(0);
  const [saved, setSaved] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const playerRef = useRef<ScanVideoPlayerHandle>(null);

  const { data, loading, generationId } = useSavedAIResult();
  const videoChapters = data?.lessonPlan?.chapters.map((chapter, index) => ({
    time: chapter.startSeconds,
    label: chapter.label?.trim() || (index === 0 ? 'Introduction' : `Lesson part ${index}`),
  })) || [];
  const totalDuration = data?.lessonPlan?.chapters.reduce(
    (total, chapter) => Math.max(total, chapter.startSeconds + chapter.durationSeconds),
    0,
  ) || 0;
  const activeChapterIndex = videoChapters.reduce(
    (active, chapter, index) => (chapter.time <= currentTime ? index : active),
    0,
  );

  const t = {
    pageBg: isDark ? 'bg-[#0F0F10]' : 'bg-white',
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textSecondary: isDark ? 'text-neutral-400' : 'text-neutral-500',
    textMuted: isDark ? 'text-neutral-500' : 'text-neutral-400',
    textFaint: isDark ? 'text-neutral-700' : 'text-neutral-300',
    cardBg: isDark ? 'bg-[#1C1D21]' : 'bg-white',
    cardBorder: isDark ? 'border-white/[0.05]' : 'border-neutral-100',
    iconBg: isDark ? 'bg-white/[0.04]' : 'bg-neutral-100',
    headerBg: isDark ? 'bg-[#0F0F10]/90' : 'bg-white/90',
    headerBorder: isDark ? 'border-white/[0.05]' : 'border-neutral-100',
    progressTrack: isDark ? 'bg-neutral-800' : 'bg-neutral-200',
    divider: isDark ? 'border-white/[0.03]' : 'border-neutral-50',
    freeBadge: isDark ? 'bg-white/[0.04] text-neutral-400 border border-white/[0.06]' : 'bg-neutral-100 text-neutral-500 border border-neutral-200',
  };

  // Auto-save when generation completes
  useEffect(() => {
    if (!user) return;
    if (!isGenerating) {
      if (!data) return;
      // The analyzer has already completed this job. Do not undo a custom name
      // when a render/remount re-runs this effect.
      if (getScanHistory().some(item => item.generationId === data.id && item.status === 'completed')) {
        setSaved(true);
        return;
      }
      recordScan({
        generationId: data.id,
        status: 'completed',
        type: 'video',
        title: data.title,
        subject: data.subject,
        uploadType: 'Image',
      });
      setSaved(true);
      setShowSaveToast(true);
      const timer = setTimeout(() => setShowSaveToast(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [isGenerating, data, user]);

  // Show save modal when generation completes and user is arriving from flow
  useEffect(() => {
    if (!user) return;
    if (!isGenerating) {
      const awaiting = sessionStorage.getItem('scan_awaiting_save') === 'true';
      if (awaiting) {
        setShowSaveModal(true);
      }
    }
  }, [isGenerating, user]);

  const handleSaveSession = (customName: string) => {
    if (!user) return;
    sessionStorage.removeItem('scan_awaiting_save');
    createStudySession({
      title: customName,
      subject: data.subject,
      topicName: data.overallSubject.topicsDetected[0],
      type: 'video',
      uploadType: 'Image',
    });
    recordScan({
      generationId: data.id,
      status: 'completed',
      type: 'video',
      title: customName,
      subject: data.subject,
      uploadType: 'Image',
    });
    setSaved(true);
    setShowSaveToast(true);
    setShowSaveModal(false);
    setTimeout(() => setShowSaveToast(false), 2500);
  };

  const handleSkipSave = () => {
    sessionStorage.removeItem('scan_awaiting_save');
    setShowSaveModal(false);
  };

  const handleManualSave = () => {
    if (!user) return;
    recordScan({
      generationId: data.id,
      status: 'completed',
      type: 'video',
      title: data.title,
      subject: data.subject,
      uploadType: 'Image',
    });
    setSaved(true);
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 2200);
  };

  const handleBack = () => {
    navigate('/history');
  };

  const handleChapterClick = (time: number) => {
    playerRef.current?.seekTo(time);
    playerRef.current?.setPlaying(true);
  };

  const handleAskAI = () => {
    const lessonContext = {
      lessonId: data.id,
      lessonTitle: data.title,
      subject: data.subject,
      topic: data.topic,
      summary: data.explanation.summary,
      keyPoints: data.explanation.steps.flatMap((step) => [
        step.title,
        ...step.content,
        step.result,
      ]).filter(Boolean).slice(0, 20),
      narration: data.lessonPlan.chapters.map((chapter) => `${chapter.label}: ${chapter.script}`).join('\n'),
    };
    const conversation = createChatConversation({
      title: `${data.topic || data.title} — Lesson questions`,
      source: 'lesson',
      lessonContext,
    }, user?.id, getStudyScope());
    navigate(`/ai-assistant?conversation=${encodeURIComponent(conversation.id)}`);
  };

  if (loading || (generationId && !data)) return <SavedLessonUnavailable loading={loading} />;
  if (!data || !data.lessonCanvas || videoChapters.length === 0) {
    return (
      <div className={`flex min-h-full items-center justify-center px-6 ${t.pageBg}`}>
        <div className={`max-w-sm rounded-2xl border ${t.cardBorder} ${t.cardBg} p-6 text-center`}>
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
            <i className="ri-error-warning-line text-xl text-red-500" />
          </div>
          <h1 className={`text-base font-bold ${t.textPrimary}`}>Lesson visual unavailable</h1>
          <p className={`mt-2 text-sm leading-relaxed ${t.textSecondary}`}>
            We couldn’t load the generated lesson. Please scan the schoolwork again and try Generate Lesson.
          </p>
          <Pressable
            onClick={() => navigate('/scan/start')}
            className="mt-5 w-full rounded-xl bg-neutral-900 py-3 text-sm font-semibold text-white"
            scale={0.97}
          >
            Scan Again
          </Pressable>
        </div>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div className={`flex flex-col min-h-full ${t.pageBg} items-center justify-center px-6 transition-colors duration-500 relative`}>
        <div className="relative mb-8">
          <div className={`w-24 h-24 rounded-full ${t.cardBg} border ${t.cardBorder} flex items-center justify-center`}>
            <div className="w-12 h-12 flex items-center justify-center">
              <i className={`ri-movie-ai-line ${t.textSecondary} text-3xl`} />
            </div>
          </div>
          <div className="absolute inset-0 rounded-full border-2 border-neutral-700/20" style={{ clipPath: `inset(0 ${100 - progress}% 0 0)` }}>
            <div className="w-full h-full rounded-full border-2 border-white" />
          </div>
          <motion.div
            className="absolute -inset-3 rounded-full border border-dashed"
            style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}
            animate={{ rotate: 360 }}
            transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
          />
        </div>

        <motion.h2
          className={`${t.textPrimary} text-lg font-bold mb-2 text-center`}
          animate={{ opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          Generating Video Lesson...
        </motion.h2>
        <p className={`${t.textMuted} text-sm text-center max-w-[260px] leading-relaxed mb-2`}>
          The AI is creating a narrated walkthrough of your homework with animated visuals.
        </p>
        <p className={`text-[11px] ${t.textFaint} text-center mb-6`}>
          Voice: {preferences.voiceStyle} &bull; Speed: {preferences.narrationSpeed}
        </p>

        {/* Free tier info during generation */}
        {!tier.downloadsEnabled && (
          <div className={`px-4 py-2 rounded-xl ${t.freeBadge} mb-4 text-[11px] text-center max-w-[280px]`}>
            <div className="flex items-center justify-center gap-1.5 mb-0.5">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-information-line text-[10px]" />
              </div>
              <span className="font-medium">Free tier</span>
            </div>
            <span className="text-[10px] opacity-80">Video will stream in-app only. Downloads require Premium.</span>
          </div>
        )}

        <div className="w-full max-w-[280px]">
          <div className={`h-1.5 ${t.progressTrack} rounded-full overflow-hidden`}>
            <motion.div
              className="h-full bg-neutral-900 rounded-full"
              style={{ width: `${Math.min(progress, 100)}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <p className={`${t.textFaint} text-xs text-center mt-3`}>{Math.round(Math.min(progress, 100))}% complete</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col min-h-full ${t.pageBg} transition-colors duration-500 relative`}>
      {/* Save Toast */}
      <AnimatePresence>
        {showSaveToast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            className="absolute top-0 left-0 right-0 z-50 flex justify-center pt-4"
          >
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-full ${isDark ? 'bg-neutral-800 border border-white/[0.08]' : 'bg-white border border-neutral-200'} shadow-lg`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-emerald-500 text-sm" />
              </div>
              <span className={`text-xs font-medium ${t.textSecondary}`}>Saved to History</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky Header */}
      <div className={`sticky top-0 z-30 ${t.headerBg} backdrop-blur-xl border-b ${t.headerBorder} transition-colors duration-500`}>
        <div className="flex items-center justify-between px-5 py-4">
          <Pressable
            onClick={handleBack}
            className={`w-9 h-9 flex items-center justify-center rounded-xl ${t.iconBg} transition-colors duration-500`}
            scale={0.88}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`ri-arrow-left-line ${t.textMuted}`} />
            </div>
          </Pressable>
          <div className="text-center">
            <h1 className={`text-sm font-bold ${t.textPrimary}`}>Visual Lesson</h1>
            <p className={`text-[11px] ${t.textSecondary}`}>{data.subject}</p>
          </div>
          {user && <Pressable
            onClick={handleManualSave}
            className={`w-9 h-9 flex items-center justify-center rounded-xl ${t.iconBg} transition-colors duration-500`}
            scale={0.88}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${saved ? 'ri-check-fill text-emerald-500' : `ri-bookmark-line ${t.textMuted}`}`} />
            </div>
          </Pressable>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          {/* Playback preference */}
          <div className="px-5 pt-4 pb-1 flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${isDark ? 'border-white/[0.06] text-[#6E6E73]' : 'border-black/[0.08] text-black/40'}`}>
              {preferences.narrationSpeed} Speed
            </span>
          </div>

          {/* Video Player Area */}
          <div className="px-5 pt-2 pb-3">
            <ScanVideoPlayer
              ref={playerRef}
              duration={totalDuration}
              chapters={videoChapters}
              title={data.title}
              lessonCanvas={data.lessonCanvas}
              chapterScripts={data.lessonPlan.chapters.map((chapter) => chapter.script)}
              narrationProvider={data.narration?.provider}
              narrationAudioUrl={data.narration?.audioUrl}
              narrationGuestAccessToken={data.narration?.guestAccessToken}
              generationId={data.id}
              onTimeUpdate={setCurrentTime}
              defaultPlaybackSpeed={narrationSpeed}
            />
              <div className="mt-3 flex items-center justify-between gap-3">
                <Pressable
                  onClick={() => handleChapterClick(videoChapters[Math.max(0, activeChapterIndex - 1)].time)}
                  disabled={activeChapterIndex === 0}
                  className={`flex-1 rounded-xl border py-2.5 text-xs font-semibold transition-opacity ${t.cardBorder} ${t.cardBg} ${activeChapterIndex === 0 ? 'opacity-40' : ''}`}
                  scale={0.97}
                >
                  <i className="ri-arrow-left-line mr-1" />
                  Previous
                </Pressable>
                <span className={`max-w-[42%] truncate text-center text-[10px] ${t.textMuted}`}>
                  {videoChapters[activeChapterIndex]?.label}
                </span>
                <Pressable
                  onClick={() => handleChapterClick(videoChapters[Math.min(videoChapters.length - 1, activeChapterIndex + 1)].time)}
                  disabled={activeChapterIndex === videoChapters.length - 1}
                  className={`flex-1 rounded-xl border py-2.5 text-xs font-semibold transition-opacity ${t.cardBorder} ${t.cardBg} ${activeChapterIndex === videoChapters.length - 1 ? 'opacity-40' : ''}`}
                  scale={0.97}
                >
                  Next
                  <i className="ri-arrow-right-line ml-1" />
                </Pressable>
              </div>
          </div>

          {/* Video Info */}
          <div className="px-5 pb-4">
            <h2 className={`text-sm font-bold ${t.textPrimary} mb-1`}>{data.title}</h2>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] ${t.textSecondary}`}>{data.subject}</span>
              <span className={`w-0.5 h-0.5 rounded-full ${t.textFaint}`} />
              <span className={`text-[11px] ${t.textMuted}`}>{formatTime(totalDuration)}</span>
            </div>
            {data.durationNotice && (
              <p className={`mt-2 text-[11px] leading-relaxed ${t.textSecondary}`}>
                {data.durationNotice}
              </p>
            )}
            {data.narration?.warning && (
              <p className={`mt-2 text-[10px] leading-relaxed ${t.textMuted}`}>
                {data.narration.warning} Visual playback and editing remain available if narration is unavailable.
              </p>
            )}
          </div>

          {/* Chapters */}
          <div className="px-5 pb-5">
            <h3 className={`text-xs font-bold ${t.textMuted} uppercase tracking-wider mb-3`}>Chapters</h3>
            <div className="space-y-1.5">
              {videoChapters.map((chapter) => {
                const isActive = currentTime >= chapter.time &&
                  currentTime < (videoChapters.find((c) => c.time > chapter.time)?.time ?? totalDuration);
                const isPast = currentTime > chapter.time;
                return (
                  <Pressable
                    key={chapter.time}
                    onClick={() => handleChapterClick(chapter.time)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-200 text-left ${
                      isActive
                        ? isDark ? 'bg-white/[0.06]' : 'bg-neutral-100'
                        : isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-neutral-50'
                    }`}
                    scale={0.98}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {isActive ? (
                        <div className="w-2.5 h-2.5 rounded-full bg-white" />
                      ) : isPast ? (
                        <i className="ri-check-line text-emerald-500 text-xs" />
                      ) : (
                        <div className="w-2 h-2 rounded-full border border-neutral-500" />
                      )}
                    </div>
                    <span className={`text-xs ${isActive ? t.textPrimary : t.textSecondary} flex-1`}>{chapter.label}</span>
                    <span className={`text-[10px] ${t.textMuted} tabular-nums`}>{formatTime(chapter.time)}</span>
                  </Pressable>
                );
              })}
            </div>
          </div>

          {/* Detected Topics */}
          <div className="px-5 pb-5">
            <div className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5 transition-colors duration-500`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-focus-3-line ${t.textMuted} text-xs`} />
                </div>
                <h3 className={`text-sm font-bold ${t.textPrimary}`}>Detected Topics</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.overallSubject.topicsDetected.map((topic) => (
                  <span
                    key={topic}
                    className={`text-[11px] font-medium px-3 py-1.5 rounded-full border ${isDark ? 'border-white/[0.05] bg-white/[0.03]' : 'border-neutral-100 bg-neutral-50'} ${t.textMuted}`}
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="px-5 pb-8 space-y-3">
            <Pressable
              onClick={() => navigate('/lesson/lesson-001')}
              className="w-full bg-neutral-900 text-white py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2"
              scale={0.97}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-2-line text-sm" />
              </div>
              <span className="whitespace-nowrap">Full Lesson on {data.subject}</span>
            </Pressable>
            <Pressable
              onClick={handleAskAI}
              className={`w-full ${t.cardBg} border ${t.cardBorder} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-500`}
              scale={0.97}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-bard-line ${t.textMuted} text-sm`} />
              </div>
              <span className={`${t.textPrimary} whitespace-nowrap`}>Ask AI About This</span>
            </Pressable>
            <Pressable
              onClick={() => navigate('/scan/start')}
              className={`w-full ${t.cardBg} border ${t.cardBorder} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-500`}
              scale={0.97}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-scan-line ${t.textMuted} text-sm`} />
              </div>
              <span className={`${t.textPrimary} whitespace-nowrap`}>Scan Another</span>
            </Pressable>
            <Pressable
              onClick={() => navigate('/history')}
              className={`w-full ${t.cardBg} border ${t.cardBorder} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-500`}
              scale={0.97}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-history-line ${t.textMuted} text-sm`} />
              </div>
              <span className={`${t.textPrimary} whitespace-nowrap`}>View History</span>
            </Pressable>
          </div>
        </motion.div>
      </div>

      <SaveSessionModal
        open={showSaveModal}
        subject={data.subject}
        topicName={data.overallSubject.topicsDetected[0]}
        type="video"
        onSave={handleSaveSession}
        onSkip={handleSkipSave}
      />

    </div>
  );
}
